import { ADR_CONSTANTS } from './constants.js';
import Logger from './logger.js';

export class Config {
	constructor() {
		this.config = { ...ADR_CONSTANTS.DEFAULTS };
		this.loaded = false;
		this.logger = new Logger(this);
		this.locked = false;
	}

	_pickPublicConfig(input) {
		if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
		const out = {};
		if (typeof input.profileId === 'string' && input.profileId.trim()) {
			out.profileId = input.profileId.trim();
		}
		if (typeof input.portalBaseUrl === 'string' && input.portalBaseUrl.trim()) {
			out.portalBaseUrl = input.portalBaseUrl.trim();
		}
		if (typeof input.sensorHash === 'string' && input.sensorHash.trim().length === 64 && /^[a-f0-9]+$/i.test(input.sensorHash.trim())) {
			out.sensorHash = input.sensorHash.trim();
		}
		// Per-site reporting floor. Accepted from the page so an application can be
		// tuned without rebuilding and republishing the bundle — the default of
		// 'medium' silently discards low-severity findings in the dispatcher, which
		// is precisely the early signal of a compromised CDN or an injected tag.
		//
		// Validated HERE rather than left to validate(), which THROWS on an invalid
		// severity: a typo in one site's config would kill sensor initialisation
		// outright. Filtering at the pick step means a bad value is ignored and the
		// default stands. Degrade, do not die.
		if (typeof input.minSeverity === 'string'
			&& Object.values(ADR_CONSTANTS.SEVERITY).includes(input.minSeverity.trim().toLowerCase())) {
			out.minSeverity = input.minSeverity.trim().toLowerCase();
		}
		return out;
	}

	async init(inputConfig = {}) {	
		if (typeof window !== 'undefined' && window.__ADR_CONFIG) {
			this.update(this._pickPublicConfig(window.__ADR_CONFIG));
		}
		this.update(this._pickPublicConfig(inputConfig));
		this.validate();
		this.loaded = true;
		this.logger.info('Configuration loaded');
		return this.getAll();
	}

	update(newConfig) {
		if (this.locked) return;
		this.config = { ...this.config, ...newConfig };
	}

	lock() {
		if (this.locked) return;
		this.locked = true;
		try {
			Object.freeze(this.config);
			Object.defineProperty(this, 'config', {
				value: this.config,
				writable: false,
				configurable: false,
				enumerable: true
			});
		} catch {
		}
		try {
			Object.freeze(this);
		} catch {
		}
	}

	_getPortalBaseUrl() {
		const apiBaseUrl = this.config.apiBaseUrl;
		if (typeof apiBaseUrl === 'string' && apiBaseUrl.trim()) return apiBaseUrl.trim();
		const portalBaseUrl = this.config.portalBaseUrl;
		if (typeof portalBaseUrl === 'string' && portalBaseUrl.trim()) return portalBaseUrl.trim();
		return '';
	}

	_isAbsoluteUrl(value) {
		if (typeof value !== 'string') return false;
		return /^https?:\/\//i.test(value.trim());
	}

	_isRelativeUrl(value) {
		if (typeof value !== 'string') return false;
		const v = value.trim();
		if (!v) return false;
		return !this._isAbsoluteUrl(v);
	}

	_resolveUrlMaybe(value) {
		if (typeof value !== 'string') return value;
		const v = value.trim();
		if (!v) return v;
		if (this._isAbsoluteUrl(v)) return v;

		const base = this._getPortalBaseUrl();
		if (!base) return v;

		try {
			return new URL(v, base).toString();
		} catch {
			return v;
		}
	}

	validate() {
		if (!this.config.endpoint || typeof this.config.endpoint !== 'string') {
			throw new Error(ADR_CONSTANTS.ERRORS.NO_ENDPOINT);
		}

		if (!this.config.profileId || typeof this.config.profileId !== 'string' || !this.config.profileId.trim()) {
			throw new Error(ADR_CONSTANTS.ERRORS.NO_PROFILE_ID);
		}
		if (!this.config.tokenEndpoint || typeof this.config.tokenEndpoint !== 'string' || !this.config.tokenEndpoint.trim()) {
			throw new Error(ADR_CONSTANTS.ERRORS.NO_TOKEN_ENDPOINT);
		}

		const needsBase =
			this._isRelativeUrl(this.config.endpoint) ||
			this._isRelativeUrl(this.config.scriptIntegrityEndpoint) ||
			this._isRelativeUrl(this.config.tokenEndpoint);
		if (needsBase && !this._getPortalBaseUrl()) {
			throw new Error(ADR_CONSTANTS.ERRORS.NO_PORTAL_BASE_URL);
		}

		this.config.endpoint = this._resolveUrlMaybe(this.config.endpoint);
		this.config.scriptIntegrityEndpoint = this._resolveUrlMaybe(this.config.scriptIntegrityEndpoint);
		this.config.tokenEndpoint = this._resolveUrlMaybe(this.config.tokenEndpoint);

		const validSeverities = Object.values(ADR_CONSTANTS.SEVERITY);
		if (!validSeverities.includes(this.config.minSeverity)) {
			throw new Error(ADR_CONSTANTS.ERRORS.INVALID_SEVERITY);
		}
		const validModes = Object.values(ADR_CONSTANTS.MODES);
		if (!validModes.includes(this.config.mode)) {
			throw new Error(ADR_CONSTANTS.ERRORS.INVALID_MODE);
		}
	}

	get(key) {
		return this.config[key];
	}

	getAll() {
		return { ...this.config };
	}

	isLoaded() {
		return this.loaded;
	}
}

export default Config;

